import type { ReactNode } from 'react';
import type { VenueChoice, VenueId } from '@/api/types';
import { cn } from '@/lib/cn';
import { venueOf } from './catalog';

const INK = '#1A1A1A';
/** The bone cut-out edge, like a blob sticker (reads on bone and on night). */
const EDGE = { stroke: '#F2EEE5', strokeWidth: 3, strokeLinejoin: 'round' as const, paintOrder: 'stroke' as const };

const MARKS: Record<VenueId, (fill: string) => ReactNode> = {
  // A cutting-chai glass.
  chai_tapri: (fill) => (
    <>
      <path d="M11 13h18l-2.6 20a2.5 2.5 0 0 1-2.5 2.2h-7.8a2.5 2.5 0 0 1-2.5-2.2Z" fill={fill} {...EDGE} />
      <path d="M12.2 19.5h15.6l-.5 3.8H12.7Z" fill={INK} />
      <path d="M17 9.5c-1.4-1.6 1.4-2.6 0-4.5M23 9.5c-1.4-1.6 1.4-2.6 0-4.5" fill="none" stroke={INK} strokeWidth={2} strokeLinecap="round" />
    </>
  ),
  // A crescent over the rooftop.
  rooftop_night: (fill) => <path d="M27.5 28.5A12 12 0 0 1 14 8a13 13 0 1 0 13.5 20.5Z" fill={fill} {...EDGE} />,
  // A low sun on the water.
  beach_sunset: (fill) => (
    <>
      <path d="M7 27a13 13 0 0 1 26 0Z" fill={fill} {...EDGE} />
      <path d="M5 31.5c3 0 3-1.6 6-1.6s3 1.6 6 1.6 3-1.6 6-1.6 3 1.6 6 1.6 3-1.6 6-1.6" fill="none" stroke={INK} strokeWidth={2.2} strokeLinecap="round" />
    </>
  ),
  // A book, open at the café.
  bookstore_cafe: (fill) => (
    <>
      <path d="M6 11c5-1.5 10-1 14 2 4-3 9-3.5 14-2v19c-5-1.5-10-1-14 2-4-3-9-3.5-14-2Z" fill={fill} {...EDGE} />
      <path d="M20 13v19" stroke={INK} strokeWidth={2} />
    </>
  ),
  // One big star.
  stargazing: (fill) => <path d="M20 5l3.6 10.4L34 19l-10.4 3.6L20 33l-3.6-10.4L6 19l10.4-3.6Z" fill={fill} {...EDGE} />,
  // A candle and its flame.
  candlelight: (fill) => (
    <>
      <rect x="14" y="18" width="12" height="18" rx="2.5" fill={fill} {...EDGE} />
      <path d="M20 4c3.5 4.2 4.6 6.6 3.6 9.3-.6 1.6-2 2.5-3.6 2.5s-3-.9-3.6-2.5C15.4 10.6 16.5 8.2 20 4Z" fill="#F2C21A" {...EDGE} />
      <path d="M20 15.8v2.2" stroke={INK} strokeWidth={1.8} />
    </>
  ),
};

/** A venue's flat poster mark (replaces the emoji in chrome). Decorative. */
export function VenueMark({ venue, size = 32, color, className }: { venue: VenueChoice | null | undefined; size?: number; color?: string; className?: string }) {
  const v = venueOf(venue);
  if (!v) return null;
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden className={cn('shrink-0', className)} data-venue-mark={v.id}>
      {MARKS[v.id](color ?? v.theme.accent)}
    </svg>
  );
}
